import {
  CustomLayoutStatus,
  ImpositionMode,
  type CustomImpositionDTO,
  type CustomImpositionLayoutState,
} from "./types";
import {
  customImpositionStateKey,
  normalizeProductType,
} from "./custom-imposition.identity";
import { printWorkingRequestCm } from "./custom-imposition.print-sheet";

export function createDefaultCustomImpositionState(
  productType = "",
): CustomImpositionLayoutState {
  return {
    mode: ImpositionMode.Automatic,
    printHouseMaterialSizeId: null,
    productType,
    positions: [],
    sheetWidthUm: 0,
    sheetLengthUm: 0,
    purchasedWidthUm: 0,
    purchasedLengthUm: 0,
    printSheetMode: "full",
    unitWidthUm: 0,
    unitHeightUm: 0,
    needsRecalculate: false,
    isStale: false,
    staleReason: null,
    lastBackendError: null,
    draft: null,
    marginXUm: 0,
    marginYUm: 0,
    bleedXUm: 0,
    bleedYUm: 0,
  };
}

export function getCustomLayoutStatus(
  state: CustomImpositionLayoutState | null | undefined,
): CustomLayoutStatus {
  if (!state || state.mode !== ImpositionMode.Custom) {
    return CustomLayoutStatus.Empty;
  }
  if (state.isStale) {
    return CustomLayoutStatus.Stale;
  }
  if (
    state.positions.length > 0 &&
    state.printHouseMaterialSizeId
  ) {
    return CustomLayoutStatus.Ready;
  }
  return CustomLayoutStatus.Empty;
}

export function isCustomLayoutReady(
  state: CustomImpositionLayoutState | null | undefined,
): boolean {
  return getCustomLayoutStatus(state) === CustomLayoutStatus.Ready;
}

export function isCustomCalculationBlocked(
  state: CustomImpositionLayoutState | null | undefined,
): boolean {
  return Boolean(state && state.mode === ImpositionMode.Custom && !isCustomLayoutReady(state));
}

export function findCustomImpositionState(
  byKey: Record<string, CustomImpositionLayoutState> | null | undefined,
  productType?: string | null,
  actionId?: string | null,
): CustomImpositionLayoutState | undefined {
  if (!byKey) {
    return undefined;
  }
  const exact = byKey[customImpositionStateKey(productType, actionId)];
  if (exact) {
    return exact;
  }
  const normalized = normalizeProductType(productType);
  return Object.values(byKey).find(
    (state) => normalizeProductType(state.productType) === normalized,
  );
}

export function getCustomCalculationBlock(
  byKey: Record<string, CustomImpositionLayoutState> | null | undefined,
): {
  blocked: boolean;
  reasonKey: string | null;
  staleReason: "material" | "unit" | null;
} {
  const blockedState = Object.values(byKey ?? {}).find(isCustomCalculationBlocked);
  if (!blockedState) {
    return { blocked: false, reasonKey: null, staleReason: null };
  }
  const reasonKey =
    blockedState.staleReason === "unit"
      ? "pricingWidget.customImposition.unitChanged"
      : blockedState.staleReason === "material"
        ? "pricingWidget.customImposition.materialChanged"
        : "pricingWidget.customImposition.calculationBlocked";
  return {
    blocked: true,
    reasonKey,
    staleReason: blockedState.staleReason,
  };
}

export function canOpenDesigner(
  mode: ImpositionMode,
  printHouseMaterialSizeId: string | null | undefined,
  sheetWidthUm: number,
  sheetLengthUm: number,
): boolean {
  return (
    mode === ImpositionMode.Custom &&
    Boolean(printHouseMaterialSizeId) &&
    sheetWidthUm > 0 &&
    sheetLengthUm > 0
  );
}

export function clonePositions<T extends { x: number; y: number; width: number; length: number }>(
  positions: T[],
): T[] {
  return positions.map((position) => ({
    ...position,
    x: position.x,
    y: position.y,
    width: position.width,
    length: position.length,
  }));
}

export function buildCustomImpositionRequest(
  state: CustomImpositionLayoutState | null | undefined,
): CustomImpositionDTO | undefined {
  if (!state || state.mode !== ImpositionMode.Custom) {
    return undefined;
  }
  if (!isCustomLayoutReady(state)) {
    return undefined;
  }

  const printWorking = printWorkingRequestCm(
    state.printSheetMode,
    state.sheetWidthUm,
    state.sheetLengthUm,
  );

  return {
    Mode: ImpositionMode.Custom,
    PrintHouseMaterialSizeId: state.printHouseMaterialSizeId as string,
    ProductType: state.productType ?? "",
    Positions: clonePositions(state.positions),
    MarginX: Math.round(state.marginXUm ?? 0),
    MarginY: Math.round(state.marginYUm ?? 0),
    PrintWorkingWidth: printWorking.PrintWorkingWidth,
    PrintWorkingLength: printWorking.PrintWorkingLength,
  };
}

export function buildCustomImpositionsForCalculate(
  byKey: Record<string, CustomImpositionLayoutState> | null | undefined,
): CustomImpositionDTO[] | undefined {
  if (!byKey) {
    return undefined;
  }

  const byProductType = new Map<string, CustomImpositionDTO>();
  Object.values(byKey).forEach((state) => {
    const payload = buildCustomImpositionRequest(state);
    if (!payload) {
      return;
    }
    const typeKey = payload.ProductType ?? "";
    if (!byProductType.has(typeKey)) {
      byProductType.set(typeKey, payload);
    }
  });

  const payloads = Array.from(byProductType.values());
  return payloads.length ? payloads : undefined;
}

/** Same attach used by use-digital-offset-price → calculate-productV2. */
export function attachCustomImpositionsToCalculationRequest<T extends Record<string, any>>(
  calculationObj: T,
  byKey: Record<string, CustomImpositionLayoutState> | null | undefined,
): { blocked: true; reasonKey: string; request: T } | { blocked: false; request: T } {
  const block = getCustomCalculationBlock(byKey);
  if (block.blocked) {
    const request = { ...calculationObj };
    delete (request as any).CustomImpositions;
    return {
      blocked: true,
      reasonKey: block.reasonKey as string,
      request,
    };
  }

  const customImpositions = buildCustomImpositionsForCalculate(byKey);
  if (!customImpositions) {
    const request = { ...calculationObj };
    delete (request as any).CustomImpositions;
    return { blocked: false, request };
  }

  return {
    blocked: false,
    request: {
      ...calculationObj,
      CustomImpositions: customImpositions,
    },
  };
}

export function requestContainsPixelLeak(payload: CustomImpositionDTO | undefined): boolean {
  if (!payload?.Positions) {
    return false;
  }
  const fields = [
    ...(payload.Positions ?? []).flatMap((position) => [
      position.x,
      position.y,
      position.width,
      position.length,
    ]),
    payload.MarginX,
    payload.MarginY,
  ];
  return fields.some((value) => !Number.isFinite(value) || !Number.isInteger(value));
}

export function requestHasRenderFields(payload: unknown): boolean {
  return /canvasX|canvasY|px|scale|pixel/i.test(JSON.stringify(payload ?? {}));
}

export function mapCustomImpositionErrorKey(raw: string | undefined): string | null {
  if (!raw) {
    return null;
  }
  const key = raw.replace(/\s/g, "");
  const map: Record<string, string> = {
    CustomImpositionMaterialSizeNotFound: "pricingWidget.customImposition.errors.materialSizeNotFound",
    CustomImpositionEmpty: "pricingWidget.customImposition.errors.empty",
    CustomImpositionOutOfBounds: "pricingWidget.customImposition.errors.outOfBounds",
    CustomImpositionInvalidUnitSize: "pricingWidget.customImposition.errors.invalidUnitSize",
    CustomImpositionOverlap: "pricingWidget.customImposition.errors.overlap",
    CustomImpositionUnsupportedContext: "pricingWidget.customImposition.errors.unsupported",
    CustomImpositionPrintWorkingCannotBeProduced:
      "pricingWidget.customImposition.errors.printWorkingCannotBeProduced",
    CustomImpositionPrintWorkingNotAcceptedByPrinter:
      "pricingWidget.customImposition.errors.printWorkingNotAcceptedByPrinter",
    CustomImpositionPrePrintCutterUnavailable:
      "pricingWidget.customImposition.errors.prePrintCutterUnavailable",
    CustomImpositionPrePrintCoveredByStock:
      "pricingWidget.customImposition.errors.prePrintCoveredByStock",
  };
  return map[key] ?? null;
}

/** Same attach used by updateProductItemDraftActionMachine / Save+Recalculate. */
export function attachCustomImpositionToMachineRequest<T extends Record<string, any>>(
  payload: T,
  state: CustomImpositionLayoutState | null | undefined,
): T {
  if (isCustomCalculationBlocked(state)) {
    const next = { ...payload };
    delete (next as any).CustomImposition;
    return next;
  }

  const custom = buildCustomImpositionRequest(state);
  if (!custom) {
    const next = { ...payload };
    delete (next as any).CustomImposition;
    return next;
  }

  return {
    ...payload,
    printHouseMaterialSizeId: custom.PrintHouseMaterialSizeId,
    CustomImposition: custom,
  };
}

export function shouldBlockUpdateActionMachine(
  state: CustomImpositionLayoutState | null | undefined,
  _payload?: Record<string, any>,
): boolean {
  if (!state || state.mode !== ImpositionMode.Custom) {
    return false;
  }
  if (!isCustomLayoutReady(state)) {
    return true;
  }
  return false;
}

export function applyBackendValidationError(
  state: CustomImpositionLayoutState,
  errorKey: string,
): CustomImpositionLayoutState {
  return {
    ...state,
    mode: ImpositionMode.Custom,
    lastBackendError: errorKey,
    needsRecalculate: true,
    isStale: false,
  };
}

export function extractCustomImpositionError(response: any): string | null {
  const candidates = collectExceptionKeyCandidates(response);
  for (const candidate of candidates) {
    const mapped = mapCustomImpositionErrorKey(candidate);
    if (mapped) {
      return mapped;
    }
  }
  return null;
}

function collectExceptionKeyCandidates(response: any): string[] {
  const keys: string[] = [];
  const push = (value: unknown) => {
    if (value == null) {
      return;
    }
    const text = value.toString();
    if (text) {
      keys.push(text);
    }
  };

  push(response?.data?.item1?.actionExceptionKey);
  push(response?.data?.item1?.exceptionKey);
  push(response?.data?.item1?.actionException?.exceptionKey);
  push(response?.data?.item1?.imposition?.actionExceptionKey);
  push(response?.data?.data?.item1?.actionExceptionKey);
  push(response?.data?.data?.item1?.exceptionKey);
  push(response?.data?.exceptionKey);
  response?.data?.data?.exceptionKey && push(response.data.data.exceptionKey);
  push(response?.exceptionKey);

  const roots = [
    response?.data?.item1,
    response?.data?.data?.item1,
    response?.data,
  ];
  for (const root of roots) {
    const actions = root?.actions ?? root?.Actions ?? [];
    for (const action of actions) {
      push(action?.actionException?.exceptionKey);
      push(action?.ActionException?.exceptionKey);
      push(action?.actionExceptionKey);
    }
    const workflows = root?.workFlows ?? root?.WorkFlows ?? [];
    for (const workflow of workflows) {
      const workflowActions = workflow?.actions ?? workflow?.Actions ?? [];
      for (const action of workflowActions) {
        push(action?.actionException?.exceptionKey);
        push(action?.ActionException?.exceptionKey);
        push(action?.actionExceptionKey);
      }
      push(workflow?.impositionMaterialResult?.imposition?.actionExceptionKey);
    }
  }

  const serialized = JSON.stringify(response ?? {});
  const nested = serialized.match(/CustomImposition[A-Za-z]+/g) ?? [];
  for (const key of nested) {
    push(key);
  }

  return keys;
}
